/**
 * API Endpoints Constants
 * 
 * Centralized API endpoint definitions.
 * Never hardcode API URLs in services or components.
 * 
 * @example
 * // Instead of: this.http.get('/api/auth/login')
 * // Use: this.http.post(API_ENDPOINTS.AUTH.LOGIN, data)
 */

import { environment } from "src/environments/environment";

/**
 * Base API Configuration
 */
export const API_CONFIG = {
  // Update these based on your environment
  BASE_URL: environment.apiBaseUrl,
  API_VERSION: 'v1',
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3
} as const;

/**
 * Build full API URL
 */
export const buildApiUrl = (endpoint: string): string => {
  return `${API_CONFIG.BASE_URL}/api/${API_CONFIG.API_VERSION}${endpoint}`;
};

/**
 * API Endpoints organized by module
 */
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: 'auth/login',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email'
  },

  // Loan Management
  LOAN: {
    APPLY: '/loan/application',
    ALL_APPLICATIONS: 'loan/application',
    GET_APPLICATION: '/loan/application',
    APPROVE_APPLICATION: '/loan/approval',
    REJECT_APPLICATION: '/loan/application/reject',
    DUE_PAYMENT: 'loan/due-payment',
    GET_ALL_LOANS: '/loan/getAll',
    LOAN_BY_ID: '/loan',
    PAYMENT_HISTORY: '/loan/payment-history',
    VIEW_DOCUMENTS: '/loan/view-doc',
    UPLOAD_DOCUMENTS: 'loan/uploadDocuments',
    DISBURSEMENT: '/loan/disbursement/getAll',
    SPECIAL_DISBURSEMENT: '/loan/spl-disbursement',
    SPECIAL_DISBURSEMENT_PAYMENT: '/loan/pay',
    COLLECTION_LIST: '/loan/collection-list',
    SETTLEMENT: '/loan/settlement',
    SETTLEMENT_ALL: '/loan/settlement/getAll',
    SETTLEMENT_DETAILS: '/loan/settlement'
  },

  // Dashboard
  DASHBOARD: {
    MAIN: 'dashboard',
    REMINDERS: 'dashboard/remainder'
  }
} as const;

/**
 * HTTP Methods
 */
export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE'
} as const;

/**
 * HTTP Headers
 */
export const HTTP_HEADERS = {
  CONTENT_TYPE: 'Content-Type',
  AUTHORIZATION: 'Authorization',
  ACCEPT: 'Accept',
  X_REQUEST_ID: 'X-Request-ID',
  X_API_KEY: 'X-API-Key'
} as const;

/**
 * Content Types
 */
export const CONTENT_TYPES = {
  JSON: 'application/json',
  FORM_DATA: 'multipart/form-data',
  URL_ENCODED: 'application/x-www-form-urlencoded',
  PDF: 'application/pdf',
  IMAGE: 'image/*'
} as const;

/**
 * HTTP Status Codes
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503
} as const;
